import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { flat } from '../helpers/object'

export type FormikHook = typeof useFormik

export function useFormManager<T extends {}>() {
  const slots = useRef<Record<string, any>>({})
  const [dirty, setDirty] = useState(false)
  const [valid, setValid] = useState(true)
  const bindCbs = useRef<{ [key: string]: FormikHook }>({})

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const register = useCallback((slotName: keyof T, instance: any) => {
    const runValidation = () => {
      const list = Object.values(slots.current)
      setValid(list.every(formik => formik.isValid))
      setDirty(list.some(formik => formik.dirty))
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      slots.current[slotName as string] = instance
      runValidation()
      // eslint-disable-next-line
    }, [instance])
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      return () => {
        delete slots.current[slotName as string]
        delete bindCbs.current[slotName as string]
        runValidation()
      }
      // eslint-disable-next-line
    }, [])
  }, [])

  const bind = useCallback(
    (slotName: keyof T) => {
      if (!bindCbs.current[slotName as string]) {
        bindCbs.current[slotName as string] = config => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const formikInstance = useFormik({
            ...config,
            validateOnMount: true,
          })
          register(slotName, formikInstance)
          return formikInstance
        }
      }
      return bindCbs.current[slotName as string]
    },
    [register],
  )

  const map = useCallback(
    (cb: (value: ReturnType<typeof useFormik>) => unknown) => {
      return Object.values(slots.current).map(cb)
    },
    [slots],
  )

  const isAllValid = useCallback(() => map(item => item.isValid).every(valid => valid), [map])

  const submitAll = useCallback(
    (cb?: (valid: boolean, result: T) => void) => {
      return Promise.all(
        map(formik => {
          return formik.submitForm()
        }),
      ).then(() => {
        const result: T = {} as T
        Object.entries(slots.current).forEach(([key, instance]) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          result[key] = instance.values

          Object.keys(flat(instance.errors)).forEach(path => {
            instance.setFieldTouched(path, true)
          })
        })
        const valid = isAllValid()
        cb && cb(valid, result as T)
        return [valid, result as T] as const
      })
    },
    [isAllValid, map],
  )

  const getValues = useCallback((slotName: number | string) => {
    return slots.current[slotName] ? slots.current[slotName].values : null
  }, [])

  const reset = useCallback(
    (form?: keyof T) => {
      if (form) {
        slots.current[form as string].resetForm()
      } else {
        map(instance => {
          instance.resetForm()
        })
      }
    },
    [map],
  )

  return {
    slots: slots.current,
    map,
    isAllValid,
    valid,
    dirty,
    submitAll,
    getValues,
    bind,
    register,
    reset,
  }
}
